import {inject, Injectable} from '@angular/core';
import {take} from 'rxjs/operators';
import {GraphQLService} from '../technique/graphql.service';
import {HttpService} from '../technique/http.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {
  PlcProduitComplementaireFacturableDTO
} from '../../dtos/produit/declinaison/complementaire/plc-produit-complementaire-facturable-dto';
import {
  LigneParametrageFacturationProduitComplementaireDTO
} from '../../dtos/produit/declinaison/complementaire/ligne-parametrage-facturation-produit-complementaire-dto';
import {
  PeriodeConditionnementNonParametreeDTO
} from '../../dtos/produit/declinaison/complementaire/periode-conditionnement-non-parametree-dto';

export const URL_POST_SAVE_LIGNES_PARAMETRAGE_PRODUIT_COMPLEMENTAIRE = `dolrest/facturation-client/parametrage/produit-complementaire/save`;

export interface LigneParametrageFacturationProduitComplementaireChanges {
  lignesToAdd: LigneParametrageFacturationProduitComplementaireDTO[];
  lignesToEdit: LigneParametrageFacturationProduitComplementaireDTO[];
  ligneIdsToDelete: number[];
  periodeIdsToDelete: number[];
}

@Injectable({
  providedIn: 'root'
})
export class FacturationProduitComplementaireService {

  private readonly graphQlSvc = inject(GraphQLService);
  private readonly httpSvc = inject(HttpService);

  /**
   * Une ligne par (PLC, produit) facturable — pas de déduplication : un même produit peut être facturable
   * pour certains PLC sélectionnés et pas pour d'autres, cette association doit rester exploitable ensuite.
   */
  findFacturablesByPlcIdList = async (plcIdList: number[]): Promise<PlcProduitComplementaireFacturableDTO[]> => {
    if (!plcIdList?.length) {
      return [];
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allPlcProduitComplementaireFacturables(filters: { plcIdList: [${plcIdList.join(',')}] }) {
          plcId,
          produitDeclinaisonId,
          libelle
        }
      }
    `).pipe(take(1)).toPromise();

    return response?.allPlcProduitComplementaireFacturables ?? [];
  };

  /**
   * Lignes de paramétrage déjà persistées — sert à distinguer ajout et modification. Liste vide ou non fournie :
   * pas de filtre PLC, donc toutes les lignes (alimente la grille de paramétrage).
   */
  findLignesByPlcIdList = async (plcIdList: number[]): Promise<LigneParametrageFacturationProduitComplementaireDTO[]> => {
    const filtersGql = plcIdList?.length ? `(filters: { plcIdList: [${plcIdList.join(',')}] })` : '';
    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allLignesParametrageFacturationProduitComplementaire${filtersGql} {
          id,
          pointFacturationId,
          plcId,
          plcLibelle,
          produitDeclinaisonId,
          produitLibelle,
          taxeId,
          taxeLibelle,
          periodeParametrageFacturationList {
            id,
            dateDebut,
            uniteDeMesureId,
            mcpConditionnementVarianteId,
            uniteDeMesure,
            prixVenteHT,
            conditionnementObsolete
          }
        }
      }
    `).pipe(take(1)).toPromise();

    return response?.allLignesParametrageFacturationProduitComplementaire ?? [];
  };

  /**
   * Périodes de conditionnement (commande) sans période de facturation portant le même conditionnement — candidates
   * à l'import dans le paramétrage de facturation, indépendamment de toute ligne déjà persistée pour ce PLC/produit.
   */
  findPeriodesNonParametreesByPlcIdList = async (plcIdList: number[]): Promise<PeriodeConditionnementNonParametreeDTO[]> => {
    if (!plcIdList?.length) {
      return [];
    }

    const response: any = await this.graphQlSvc.sendQuery(`
      {
        allPeriodesConditionnementNonParametreesProduitComplementaire(filters: { plcIdList: [${plcIdList.join(',')}] }) {
          plcId,
          produitDeclinaisonId,
          dateDebut,
          uniteDeMesureId,
          mcpConditionnementVarianteId,
          uniteDeMesure
        }
      }
    `).pipe(take(1)).toPromise();

    return response?.allPeriodesConditionnementNonParametreesProduitComplementaire ?? [];
  };

  /**
   * HttpService filtre les réponses en erreur (technique ou métier) de l'observable : sur échec, toPromise()
   * résout `undefined` plutôt que de rejeter. Sans ce contrôle, l'appelant croirait à un enregistrement réussi
   * de zéro ligne au lieu d'un échec.
   */
  saveLignes = async (changes: LigneParametrageFacturationProduitComplementaireChanges): Promise<LigneParametrageFacturationProduitComplementaireDTO[]> => {
    const response: ResponseWrapper<LigneParametrageFacturationProduitComplementaireDTO> =
      await this.httpSvc.post(URL_POST_SAVE_LIGNES_PARAMETRAGE_PRODUIT_COMPLEMENTAIRE, changes).pipe(take(1)).toPromise();
    if (!response || response.inError) {
      throw new Error('Échec de l\'enregistrement des lignes de paramétrage produit complémentaire');
    }
    return response.resultList ?? [];
  };
}
